import React from 'react';
import { useState } from 'react';
import {
  updateUserRole,
  updateAccountStatus,
  approveUser,
  renewSubscription,
  suspendUser,
  blockUser
} from '../../utils/firebaseService';
import { auth } from '../../config/firebase';
import { ACCOUNT_STATUS } from '../../constants/accountStatus';
import {
  calculateDaysRemaining,
  formatExpirationDate,
  formatBillingCycle,
  formatPlan,
  getSubscriptionStatus,
  isExpiringSoon,
  shouldBeSuspended,
  getDaysRemaining,
  formatSubscriptionStatus,
  isSubscriptionExpired,
} from '../../utils/subscriptionUtils';
import type { UserData }from '../../types/UserData';
import {getSubscriptionHealth} from '../../utils/subscriptionHealth';
import {
  getSubscriptionHealthLabel,
  getSubscriptionHealthColor,
  getSubscriptionHealthBadge
} from '../../utils/subscriptionHealthUI';
import {SUBSCRIPTION_HEALTH} from '../../constants/subscriptionHealth';
import Toast, { ToastTone } from '../../components/feedback/Toast';


interface Props {
  users: UserData[];
  refreshUsers: () => Promise<void>;
  hasMore: boolean;
  onLoadMore: () => Promise<void>;
  loadingMore: boolean;
}

const UsersSection: React.FC<Props> = ({
  users,
  refreshUsers,
  hasMore,
  onLoadMore,
  loadingMore,
}) => {
  const [feedback, setFeedback] = useState<{ message: string; tone: ToastTone } | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const handlePromoteAdmin = async (
  uid: string) => {
  try {
    setIsProcessing(true);
    await updateUserRole(uid, 'admin');
    await refreshUsers();
    setSelectedUserAction(null);
    setFeedback({ message: 'Usuário promovido a administrador com sucesso.', tone: 'success' });
  } catch (error) {
    console.error(error);
    setFeedback({ message: 'Não foi possível promover o usuário a administrador. Tente novamente.', tone: 'error' });
  } finally {
    setIsProcessing(false);
  }
};

  const handleRemoveAdmin = async (
  uid: string) => {
  try {
    setIsProcessing(true);
    await updateUserRole(uid, 'user');
    await refreshUsers();
    setSelectedUserAction(null);
    setFeedback({ message: 'Permissão de administrador removida com sucesso.', tone: 'success' });
  } catch (error) {
    console.error(error);
    setFeedback({ message: 'Não foi possível remover a permissão de administrador. Tente novamente.', tone: 'error' });
  } finally {
    setIsProcessing(false);
  }
};

  const handleUpdateStatus = async (
  uid: string,
  status: UserData['accountStatus']
) => {

  try {
    setIsProcessing(true);
    await updateAccountStatus(uid, status);
    await refreshUsers();
    setSelectedUserAction(null);
    setFeedback({ message: 'Conta reativada com sucesso.', tone: 'success' });
  } catch (error) {
    const code = error instanceof Error ? error.message : '';
    setFeedback({
      message: code.includes('ACTIVE_STATUS_REQUIRES_VALID_SUBSCRIPTION')
        ? 'Não é possível reativar a conta sem uma assinatura válida. Renove a assinatura antes de reativar o acesso.'
        : 'Não foi possível atualizar o status da conta.',
      tone: 'error',
    });
  } finally {
    setIsProcessing(false);
  }
};

const handleApproveUser = async (
  uid: string
) => {

  const adminUid = auth.currentUser?.uid;
  
  // CRÍTICO: Sem UID do admin, não pode prosseguir
  // Auditoria depende de saber quem aprovou
  if (!adminUid) {
    setFeedback({ message: 'Administrador não autenticado.', tone: 'error' });
    return;
  }

  try {
    setIsProcessing(true);
    await approveUser(uid, adminUid);
    await refreshUsers();
    setSelectedUserAction(null);
    setFeedback({ message: 'Conta aprovada com sucesso.', tone: 'success' });
  } catch (error) {
    console.error(error);
    setFeedback({ message: 'Não foi possível aprovar a conta. Tente novamente.', tone: 'error' });
  } finally {
    setIsProcessing(false);
  }

};

const handleRenewSubscription =
async (
  uid: string
) => {

  const adminUid = auth.currentUser?.uid;
  if (!adminUid) {
    setFeedback({ message: 'Administrador não autenticado.', tone: 'error' });
    return;
  }

  try {
    setIsProcessing(true);
    await renewSubscription(uid, adminUid);
    await refreshUsers();
    setSelectedUserAction(null);
    setFeedback({ message: 'Assinatura renovada com sucesso.', tone: 'success' });
  } catch (error) {
    console.error(error);
    setFeedback({ message: 'Não foi possível renovar a assinatura. Tente novamente.', tone: 'error' });
  } finally {
    setIsProcessing(false);
  }

};

const handleSuspendUser =
async (
  uid: string
) => {

  const adminUid = auth.currentUser?.uid;
  if (!adminUid) {
    setFeedback({ message: 'Administrador não autenticado.', tone: 'error' });
    return;
  }

  try {
    setIsProcessing(true);
    await suspendUser(uid, adminUid);
    await refreshUsers();
    setSelectedUserAction(null);
    setFeedback({ message: 'Conta suspensa com sucesso.', tone: 'success' });
  } catch (error) {
    console.error(error);
    setFeedback({ message: 'Não foi possível suspender a conta. Tente novamente.', tone: 'error' });
  } finally {
    setIsProcessing(false);
  }

};

const handleBlockUser =
async (uid: string
) => {

  const reason =
  prompt(
      'Motivo do bloqueio:'
    );

  if (!reason) return;

  const adminUid = auth.currentUser?.uid;
  if (!adminUid) {
    setFeedback({ message: 'Administrador não autenticado.', tone: 'error' });
    return;
  }

  try {
    setIsProcessing(true);
    await blockUser(uid, reason, adminUid);
    await refreshUsers();
    setSelectedUserAction(null);
    setFeedback({ message: 'Conta bloqueada com sucesso.', tone: 'success' });
  } catch (error) {
    console.error(error);
    setFeedback({ message: 'Não foi possível bloquear a conta. Tente novamente.', tone: 'error' });
  } finally {
    setIsProcessing(false);
  }

};

  const [selectedUserData, setSelectedUserData] =
useState<UserData | null>(null);
const [selectedUserAction, setSelectedUserAction] =
useState<UserData | null>(null);
const [healthFilter, setHealthFilter] =
useState<string>('all');
const getStatusLabel = (
status: UserData['accountStatus']
) => {

  switch (status) {

    case ACCOUNT_STATUS.ACTIVE:
      return 'Ativo';
    case ACCOUNT_STATUS.BLOCKED:
      return 'Bloqueado';
    case ACCOUNT_STATUS.SUSPENDED:
      return 'Suspenso';
    case ACCOUNT_STATUS.PENDING:
      return 'Pendente';

    default:
      return 'Desconhecido';
  }
};

const getStatusColor = (
  status: UserData['accountStatus']
) => {

  switch (status) {

    case ACCOUNT_STATUS.ACTIVE:
      return 'text-green-400';

    case ACCOUNT_STATUS.BLOCKED:
      return 'text-red-400';

    case ACCOUNT_STATUS.SUSPENDED:
      return 'text-yellow-400';

    case ACCOUNT_STATUS.PENDING:
      return 'text-blue-400';

    default:
      return 'text-slate-400';
  }
};

const healthyUsers =
  users.filter(
    user =>
      getSubscriptionHealth(user)
      ===
      SUBSCRIPTION_HEALTH.HEALTHY
  ).length;

const warningUsers =
  users.filter(
    user =>
      getSubscriptionHealth(user)
      ===
      SUBSCRIPTION_HEALTH.WARNING
  ).length;

const expiredUsers =
  users.filter(
    user =>
      getSubscriptionHealth(user)
      ===
      SUBSCRIPTION_HEALTH.EXPIRED
  ).length;

const pendingUsers =
  users.filter(
    user =>
      getSubscriptionHealth(user)
      ===
      SUBSCRIPTION_HEALTH.PENDING
  ).length;

  const filteredUsers =
healthFilter === 'all'

? users

: users.filter(
    user =>
      getSubscriptionHealth(user)
      === healthFilter
  );

  return (
    <>
      {feedback && <Toast message={feedback.message} tone={feedback.tone} onClose={() => setFeedback(null)} />}
      <div className="mb-10">

 <h1 className="text-4xl font-bold">
  Usuários
 </h1>

        <p className="text-slate-400 mt-2">
          Gerenciamento de contas da Nexora.
        </p>

      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">

  <div
  onClick={() => setHealthFilter('healthy')
  }
  className="
    bg-slate-900
    border
    border-slate-800
    rounded-2xl
    p-5
    cursor-pointer
    hover:border-green-400
    transition
  "
>
    <p className="text-slate-400 text-sm mb-2">
      🟢 Saudáveis
    </p>

    <p className="text-3xl font-bold text-green-400">
      {healthyUsers}
    </p>

  </div>

  <div
  onClick={() => setHealthFilter('warning')}
  className="
    bg-slate-900
    border
    border-slate-800
    rounded-2xl
    p-5
    cursor-pointer
    hover:border-yellow-400
    transition
  "
>
    <p className="text-slate-400 text-sm mb-2">
      🟡 Expirando
    </p>

    <p className="text-3xl font-bold text-yellow-400">
      {warningUsers}
    </p>

  </div>

  <div
  onClick={() => setHealthFilter('healthy')
  }
  className="
    bg-slate-900
    border
    border-slate-800
    rounded-2xl
    p-5
    cursor-pointer
    hover:border-green-400
    transition
  "
>

    <p className="text-slate-400 text-sm mb-2">
      🔴 Expiradas
    </p>

    <p className="text-3xl font-bold text-red-400">
      {expiredUsers}
    </p>

  </div>

  <div
  onClick={() => setHealthFilter('pending')}
  className="
    bg-slate-900
    border
    border-slate-800
    rounded-2xl
    p-5
    cursor-pointer
    hover:border-blue-400
    transition
  "
>

    <p className="text-slate-400 text-sm mb-2">
      🔵 Pendentes
    </p>

    <p className="text-3xl font-bold text-blue-400">
      {pendingUsers}
    </p>

  </div>

</div>

      <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">

        <table className="w-full">

          <thead className="bg-slate-950">

            <tr>

              <th className="text-left p-4">
                Nome
              </th>

              <th className="text-left p-4">
                Email
              </th>

              <th className="text-left p-4">
                Cargo
              </th>

              <th className="text-left p-4">
                Status
              </th>

              <th className="text-left p-4">
               Saúde
              </th>

              <th className="text-left p-4">
              Dados
              </th>

              <th className="text-left p-4">
                Ações
              </th>

            </tr>

          </thead>

          <tbody>

            {filteredUsers.length === 0 && (
              <tr>
                <td colSpan={7} className="p-8 text-center text-slate-400">Nenhum usuário encontrado para este filtro.</td>
              </tr>
            )}

            {filteredUsers.map(user => (

              <tr
                key={user.uid}
                className="border-t border-slate-800"
              >

                <td className="p-4">
                  {user.name}
                </td>

                <td className="p-4">
                  {user.email}
                </td>

                <td className="p-4">
                  {user.role || 'user'}
                </td>

                <td className="p-4">

                  <span
                    className={
                      getStatusColor(user.accountStatus)
                    }
                  >

      {getStatusLabel(user.accountStatus)}

                  </span>

                </td>

                <td className="p-4">

  {(() => {

    const health =
      getSubscriptionHealth(user);

    return (

      <span
        className={
          getSubscriptionHealthColor(
            health
          )
        }
      >

        {getSubscriptionHealthBadge(
          health
        )}{' '}

        {getSubscriptionHealthLabel(
          health
        )}

      </span>

    );

  })()}

</td>

                <td className="p-4">

  <button

    onClick={() =>
      setSelectedUserData(user)
    }

    className="text-blue-400 hover:text-blue-300"

  >

    Ver Dados

  </button>

</td>

<td className="p-4">

  <button

    onClick={() =>
    setSelectedUserAction(user)
    }

    className="text-purple-400 hover:text-purple-300"

  >

    Gerenciar

  </button>

</td>

 </tr>
))}

</tbody>

 </table>

</div>

      {hasMore && (
        <div className="mt-6 flex justify-center">
          <button type="button" onClick={() => void onLoadMore()} disabled={loadingMore} className="px-4 py-2 rounded-lg bg-slate-800 text-slate-100 hover:bg-slate-700 disabled:opacity-50">
            {loadingMore ? 'Carregando...' : 'Carregar mais'}
          </button>
        </div>
      )}

{selectedUserData && (

  <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">

    <div className="bg-[#0f172a] p-6 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">

      <h2 className="text-2xl font-bold mb-6">
        Dados do Usuário
      </h2>

      <div className="space-y-4">

        <div>
          <p className="text-slate-400 text-sm mb-1">
            Nome
          </p>
          <p className="font-medium">
            {selectedUserData?.name}
          </p>
        </div>

        <div>
          <p className="text-slate-400 text-sm mb-1">
            Email
          </p>
          <p className="font-medium">
            {selectedUserData?.email}
          </p>
        </div>

        <div>
          <p className="text-slate-400 text-sm mb-1">
            UID
          </p>
          <p className="font-mono text-sm break-all">
            {selectedUserData?.uid}
          </p>
        </div>

        <div>
          <p className="text-slate-400 text-sm mb-1">
            Cargo
          </p>
          <p className="font-medium">
            {selectedUserData?.role || 'user'}
          </p>
        </div>

        <div>
          <p className="text-slate-400 text-sm mb-1">
            Status
          </p>
          <p className={getStatusColor(selectedUserData?.accountStatus)}>
            {getStatusLabel(selectedUserData?.accountStatus)}
          </p>
        </div>

        <div>
          <p className="text-slate-400 text-sm mb-1">
            Criado em
          </p>
          <p className="font-medium">
            {selectedUserData.createdAt?.toDate
              ? selectedUserData.createdAt
                  .toDate()
                  .toLocaleString('pt-BR')
              : 'Não disponível'}
          </p>
        </div>

        <div>
          <p className="text-slate-400 text-sm mb-1">
            Último Login
          </p>
          <p className="font-medium">
            {selectedUserData.lastLogin?.toDate
              ? selectedUserData.lastLogin
                  .toDate()
                  .toLocaleString('pt-BR')
              : 'Não disponível'}
          </p>
        </div>

        <div>

  <p className="text-slate-400 text-sm mb-1">
    Aprovado em
  </p>

  <p className="font-medium">

    {selectedUserData.approvedAt?.toDate

      ? selectedUserData.approvedAt
          .toDate()
          .toLocaleString('pt-BR')

      : 'Não aprovado'}

  </p>

</div>

 <div>

  <p className="text-slate-400 text-sm mb-1">
    Aprovado por
  </p>

  <p className="font-medium">

    {selectedUserData.approvedBy ||
      'Não disponível'}

  </p>

</div>

 <div>

  <p className="text-slate-400 text-sm mb-1">
    Expira em
  </p>

  <p className="font-medium">

    {selectedUserData.subscriptionExpiresAt?.toDate

      ? selectedUserData.subscriptionExpiresAt
          .toDate()
          .toLocaleString('pt-BR')

      : 'Sem assinatura'}

  </p>

</div>

        {/* FASE 7: Seção de Assinatura - Leitura Apenas */}
        <div className="border-t border-slate-800 pt-4 mt-4">
          <p className="text-slate-400 text-sm font-semibold mb-3 uppercase tracking-wider">
            Informações de Assinatura
          </p>

          <div className="space-y-3">
            <div>
              <p className="text-slate-400 text-sm mb-1">
                Plano
              </p>
              <p className="font-medium">
                {formatPlan(selectedUserData.subscriptionPlan)}
              </p>
            </div>

            <div>
              <p className="text-slate-400 text-sm mb-1">
                Ciclo de Faturamento
              </p>
              <p className="font-medium">
                {formatBillingCycle(selectedUserData.billingCycle)}
              </p>
            </div>

            <div>
              <p className="text-slate-400 text-sm mb-1">
                Data de Início
              </p>
              <p className="font-medium">
                {selectedUserData.subscriptionStartedAt?.toDate
                  ? selectedUserData.subscriptionStartedAt
                      .toDate()
                      .toLocaleString('pt-BR')
                  : 'N/A'}
              </p>
            </div>

            <div>
              <p className="text-slate-400 text-sm mb-1">
                Data de Expiração
              </p>
              <p className="font-medium">
                {formatExpirationDate(selectedUserData.subscriptionExpiresAt)}
              </p>
            </div>

            <div>
              <p className="text-slate-400 text-sm mb-1">
                Dias Restantes
              </p>
              <p className={`font-medium ${
                calculateDaysRemaining(selectedUserData.subscriptionExpiresAt) > 0
                  ? 'text-green-400'
                  : 'text-red-400'
              }`}>
                {calculateDaysRemaining(selectedUserData.subscriptionExpiresAt)} dias
              </p>
            </div>

            <div>
              <p className="text-slate-400 text-sm mb-1">
                Renovações
              </p>
              <p className="font-medium">
                {selectedUserData.renewalCount ?? 0}
              </p>
            </div>

            <div>
              <p className="text-slate-400 text-sm mb-1">
                Próxima Renovação
              </p>
              <p className="font-medium">
                {selectedUserData.nextRenewalAt?.toDate
                  ? selectedUserData.nextRenewalAt
                      .toDate()
                      .toLocaleString('pt-BR')
                  : 'N/A'}
              </p>
            </div>
          </div>
        </div>

        {/* FASE 5: Diagnóstico de Assinatura - Leitura Apenas */}
        <div className="border-t border-slate-800 pt-4 mt-4">
          <p className="text-slate-400 text-sm font-semibold mb-3 uppercase tracking-wider">
            Diagnóstico de Assinatura
          </p>

          <div className="space-y-3">
            <div>
              <p className="text-slate-400 text-sm mb-1">
                Status
              </p>
              <p className={`font-medium px-2 py-1 rounded w-fit ${
                (() => {
                  const status = getSubscriptionStatus(selectedUserData.subscriptionExpiresAt);
                  switch (status) {
                    case 'valid':
                      return 'bg-green-500/20 text-green-400';
                    case 'expiring_soon':
                      return 'bg-yellow-500/20 text-yellow-400';
                    case 'expired':
                      return 'bg-red-500/20 text-red-400';
                    default:
                      return 'bg-slate-600/20 text-slate-400';
                  }
                })()
              }`}>
                {formatSubscriptionStatus(getSubscriptionStatus(selectedUserData.subscriptionExpiresAt))}
              </p>
            </div>

            <div>
              <p className="text-slate-400 text-sm mb-1">
                Dias Restantes
              </p>
              <p className={`font-medium ${
                getDaysRemaining(selectedUserData.subscriptionExpiresAt) > 0
                  ? 'text-green-400'
                  : getDaysRemaining(selectedUserData.subscriptionExpiresAt) === 0
                  ? 'text-yellow-400'
                  : 'text-red-400'
              }`}>
                {getDaysRemaining(selectedUserData.subscriptionExpiresAt)} dias
              </p>
            </div>

            <div>
              <p className="text-slate-400 text-sm mb-1">
                Expirando em Breve?
              </p>
              <p className={`font-medium ${
                isExpiringSoon(selectedUserData.subscriptionExpiresAt)
                  ? 'text-yellow-400'
                  : 'text-slate-400'
              }`}>
                {isExpiringSoon(selectedUserData.subscriptionExpiresAt) ? 'Sim' : 'Não'}
              </p>
            </div>

            <div>
              <p className="text-slate-400 text-sm mb-1">
                Deveria estar Suspenso?
              </p>
              <p className={`font-medium px-2 py-1 rounded w-fit ${
                shouldBeSuspended(selectedUserData.subscriptionExpiresAt)
                  ? 'bg-red-500/20 text-red-400'
                  : 'bg-green-500/20 text-green-400'
              }`}>
                {shouldBeSuspended(selectedUserData.subscriptionExpiresAt) ? 'SIM - Expirado' : 'Não'}
              </p>
              <p className="text-slate-500 text-xs mt-1">
                Apenas indicativo. Admin suspende manualmente.
              </p>
            </div>
          </div>
        </div>

        {selectedUserData.accountStatusReason && (
          <div className="border-t border-slate-800 pt-4 mt-4">
            <p className="text-slate-400 text-sm mb-1">
              Motivo do Bloqueio/Suspensão
            </p>
            <p className="text-red-400 font-medium">
              {selectedUserData.accountStatusReason}
            </p>
          </div>
        )}
      </div>

      <button
        onClick={() => setSelectedUserData(null)}
        className="mt-6 w-full px-4 py-2 rounded-xl bg-slate-700 hover:bg-slate-600"
      >
        Fechar
      </button>

    </div>

  </div>

)}


{selectedUserAction && (

  <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
    <div className="bg-[#0f172a] p-6 rounded-2xl w-full max-w-lg">

      <h2 className="text-2xl font-bold mb-6">
        Gerenciamento da Conta
      </h2>

     

      {/* Card do Usuário Selecionado */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 mb-6">
        <p className="text-slate-400 text-sm mb-3">
          Usuário Selecionado
        </p>
        <div className="space-y-2">
          <p className="font-semibold">
            {selectedUserAction.name}
          </p>
          <p className="text-slate-400 text-sm">
            {selectedUserAction.email}
          </p>
        </div>
      </div>


      <div className="border-t border-slate-800 my-6"></div>

      {/* Ciclo da Conta */}

<h3 className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-4">
  Ciclo da Conta
</h3>

<div className="space-y-3 mb-6">

  {selectedUserAction.accountStatus === ACCOUNT_STATUS.PENDING && (

    <>

      <button
        onClick={() =>
          handleApproveUser(
            selectedUserAction.uid
          )
        }
        disabled={isProcessing}
        className="w-full px-4 py-3 rounded-xl bg-green-500/20 text-green-400 hover:bg-green-500/30"
      >
        Aprovar Conta
      </button>

      <button
        onClick={() =>
          handleBlockUser(
            selectedUserAction.uid
          )
        }
        disabled={isProcessing}
        className="w-full px-4 py-3 rounded-xl bg-red-500/20 text-red-400 hover:bg-red-500/30"
      >
        Bloquear Conta
      </button>

    </>

  )}

  {selectedUserAction.accountStatus === ACCOUNT_STATUS.ACTIVE && (

    <>

      <button
        onClick={() =>
          handleRenewSubscription(
            selectedUserAction.uid
          )
        }
        disabled={isProcessing}
        className="w-full px-4 py-3 rounded-xl bg-blue-500/20 text-blue-400 hover:bg-blue-500/30"
      >
        Renovar Assinatura
      </button>

      <button
        onClick={() =>
          handleSuspendUser(
            selectedUserAction.uid
          )
        }
        disabled={isProcessing}
        className="w-full px-4 py-3 rounded-xl bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30"
      >
        Suspender Conta
      </button>

      <button
        onClick={() =>
          handleBlockUser(
            selectedUserAction.uid
          )
        }
        disabled={isProcessing}
        className="w-full px-4 py-3 rounded-xl bg-red-500/20 text-red-400 hover:bg-red-500/30"
      >
        Bloquear Conta
      </button>

    </>

  )}

  {selectedUserAction.accountStatus === ACCOUNT_STATUS.SUSPENDED && (

    <>

      <button
        onClick={() => {
          if (isSubscriptionExpired(selectedUserAction.subscriptionExpiresAt)) {
            void handleRenewSubscription(selectedUserAction.uid);
            return;
          }
          void handleUpdateStatus(selectedUserAction.uid, ACCOUNT_STATUS.ACTIVE);
        }}
        disabled={isProcessing}
        className="w-full px-4 py-3 rounded-xl bg-green-500/20 text-green-400 hover:bg-green-500/30"
      >
        {isSubscriptionExpired(selectedUserAction.subscriptionExpiresAt)
          ? 'Renovar e reativar acesso'
          : 'Reativar Conta'}
      </button>

      <button
        onClick={() =>
          handleBlockUser(
            selectedUserAction.uid
          )
        }
        disabled={isProcessing}
        className="w-full px-4 py-3 rounded-xl bg-red-500/20 text-red-400 hover:bg-red-500/30"
      >
        Bloquear Conta
      </button>

    </>

  )}

  {selectedUserAction.accountStatus === ACCOUNT_STATUS.BLOCKED && (

    <button
      onClick={() => {
        if (isSubscriptionExpired(selectedUserAction.subscriptionExpiresAt)) {
          void handleRenewSubscription(selectedUserAction.uid);
          return;
        }
        void handleUpdateStatus(selectedUserAction.uid, ACCOUNT_STATUS.ACTIVE);
      }}
      disabled={isProcessing}
      className="w-full px-4 py-3 rounded-xl bg-green-500/20 text-green-400 hover:bg-green-500/30"
    >
      {isSubscriptionExpired(selectedUserAction.subscriptionExpiresAt)
        ? 'Renovar e reativar acesso'
        : 'Reativar Conta'}
    </button>

  )}

</div>

      <div className="border-t border-slate-800 my-6"></div>

      {/* Permissões */}
      <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-4">
        Permissões
      </h3>

      <div className="flex gap-3 mb-6">
        {selectedUserAction.role === 'admin' ? (
          <button
            onClick={() =>
              handleRemoveAdmin(selectedUserAction.uid)
            }
            disabled={isProcessing}
            className="px-4 py-2 rounded-xl bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30"
          >
            Remover Admin
          </button>
        ) : (
          <button
            onClick={() =>
              handlePromoteAdmin(selectedUserAction.uid)
            }
            disabled={isProcessing}
            className="px-4 py-2 rounded-xl bg-purple-500/20 text-purple-400 hover:bg-purple-500/30"
          >
            Tornar Admin
          </button>
        )}
      </div>

      <button
        onClick={() => setSelectedUserAction(null)}
        className="w-full px-4 py-3 rounded-xl bg-slate-700 hover:bg-slate-600"
      >
        Fechar
      </button>

    </div>
  </div>
)}

    </>
  );
};

export default UsersSection;
